import React, { ChangeEvent, FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';

interface Props {
  ParagrapheMessage: string;
  setParagrapheMessage: (value: string) => void;
  Nom_Complet: string;
  setNom_Complet: (value: string) => void;
  Tele: string;
  setTele: (value: string) => void;
  Password: string;
  setPassword: (value: string) => void;
  EmailAccount: string;
  setEmailAccount: (value: string) => void;
  Adresse: string;
  setAdresse: (value: string) => void;
  showSuccessPopup: boolean;
  ShowEreurPopup: boolean;
  setContentMessage: (value: string) => void;
  setShowSuccessPopup: (value: boolean) => void;
  setShowEreurPopup: (value: boolean) => void;
  handleSubmit: (e: FormEvent) => void;
  ContentMessage: string;
  sendingOtp: boolean;
}

/* ─── Floating label field ───────────────────────────────────────── */
const Field: React.FC<{
  label: string;
  type?: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
  delay?: number;
}> = ({ label, type = 'text', value, onChange, delay = 0 }) => (
  <motion.div
    className="field"
    initial={{ opacity: 0, x: -18 }}
    animate={{ opacity: 1, x: 0 }}
    transition={{ duration: 0.45, delay }}
  >
    <input
      type={type}
      className="field__input"
      value={value}
      onChange={onChange}
      placeholder=" "
      required
    />
    <label className="field__label">{label}</label>
    <span className="field__line" />
  </motion.div>
);

/* ─── Component ──────────────────────────────────────────────────── */
const CreateAccountVisiteur: React.FC<Props> = ({
  ContentMessage,
  ParagrapheMessage,
  Nom_Complet,
  setNom_Complet,
  Tele,
  setTele,
  Password,
  setPassword,
  setEmailAccount,
  EmailAccount,
  Adresse,
  setAdresse,
  showSuccessPopup,
  ShowEreurPopup,
  handleSubmit,
  sendingOtp,
}) => {
  const [showPass, setShowPass] = useState(false);

  return (
    <PageWrapper>
      {/* ── decorative blobs ── */}
      <Blob className="blob blob--a" />
      <Blob className="blob blob--b" />

      <motion.div
        className="card"
        initial={{ opacity: 0, y: 40, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      >
        {/* Header */}
        <div className="card__header">
          <motion.div
            className="badge"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: 'spring', stiffness: 260, damping: 20 }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </motion.div>
          <motion.h1
            className="card__title"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.5 }}
          >
            Créer un compte
          </motion.h1>
          <motion.p
            className="card__sub"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.35, duration: 0.5 }}
          >
            Rejoignez-nous et accédez à toutes les fonctionnalités.
          </motion.p>
        </div>

        {/* Form */}
        <form className="card__form" onSubmit={handleSubmit} noValidate>
          <div className="grid-2">
            <Field
              label="Nom complet"
              value={Nom_Complet}
              onChange={(e) => setNom_Complet(e.target.value)}
              delay={0.15}
            />
            <Field
              label="Téléphone"
              type="tel"
              value={Tele}
              onChange={(e) => setTele(e.target.value)}
              delay={0.2}
            />
          </div>

          {/* Password with toggle */}
          <motion.div
            className="field"
            initial={{ opacity: 0, x: -18 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.45, delay: 0.25 }}
          >
            <input
              type={showPass ? 'text' : 'password'}
              className="field__input"
              value={Password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder=" "
              required
            />
            <label className="field__label">Mot de passe</label>
            <span className="field__line" />
            <button
              type="button"
              className="eye-btn"
              onClick={() => setShowPass((p) => !p)}
              aria-label="Afficher le mot de passe"
            >
              {showPass ? (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94" />
                  <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </motion.div>

          {/* Email notice */}
          <motion.div
            className="notice"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
              <circle cx="12" cy="12" r="10" />
              <line x1="12" y1="8" x2="12" y2="12" />
              <line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            Votre adresse e-mail est requise pour sécuriser votre compte.
          </motion.div>

          <Field
            label="Adresse e-mail"
            type="email"
            value={EmailAccount}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmailAccount(e.target.value)}
            delay={0.35}
          />

          <Field
            label="Adresse postale"
            value={Adresse}
            onChange={(e) => setAdresse(e.target.value)}
            delay={0.4}
          />

          <motion.button
            type="submit"
            className="btn-submit"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.4 }}
            whileHover={sendingOtp ? {} : { scale: 1.025, boxShadow: '0 8px 32px rgba(212,175,55,0.35)' }}
            whileTap={sendingOtp ? {} : { scale: 0.97 }}
            disabled={sendingOtp}
          >
            {sendingOtp ? (
              <span className="loader-spinner"></span>
            ) : (
              <>
                <span>Créer mon compte</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
                  <line x1="5" y1="12" x2="19" y2="12" />
                  <polyline points="12 5 19 12 12 19" />
                </svg>
              </>
            )}
          </motion.button>

          {/* Feedback */}
          <AnimatePresence>
            {showSuccessPopup && (
              <motion.div
                className="feedback feedback--success"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                <span>Bravo ! Votre compte est désormais actif.</span>
              </motion.div>
            )}
            {ShowEreurPopup && (
              <motion.div
                className="feedback feedback--error"
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <circle cx="12" cy="12" r="10" />
                  <line x1="12" y1="8" x2="12" y2="12" />
                  <line x1="12" y1="16" x2="12.01" y2="16" />
                </svg>
                <div>
                  <strong>{ContentMessage}</strong>
                  {ParagrapheMessage && <p>{ParagrapheMessage}</p>}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </form>

        {/* Footer */}
        <motion.p
          className="card__footer"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.55 }}
        >
          Déjà inscrit ?{' '}
          <Link to="/connecter_compte" className="card__link">
            Se connecter
          </Link>
        </motion.p>
      </motion.div>
    </PageWrapper>
  );
};

/* ─── Keyframes ──────────────────────────────────────────────────── */
const floatA = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%       { transform: translate(30px, -40px) scale(1.08); }
`;
const floatB = keyframes`
  0%, 100% { transform: translate(0, 0) scale(1); }
  50%       { transform: translate(-25px, 30px) scale(1.05); }
`;

/* ─── Styled Components ──────────────────────────────────────────── */
const Blob = styled.div``;

const PageWrapper = styled.div`
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@400;600;700&family=DM+Sans:wght@300;400;500&display=swap');

  --gold:        #d4af37;
  --gold-light:  #f0d060;
  --gold-dim:    rgba(212,175,55,0.15);
  --navy:        #0b0f1e;
  --navy-card:   #111827;
  --navy-field:  #1a2236;
  --text:        #e8eaf0;
  --text-muted:  #8892a4;
  --success:     #10b981;
  --error:       #f87171;
  --radius-lg:   20px;
  --radius-sm:   10px;

  font-family: 'DM Sans', sans-serif;
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 16px;
  position: relative;
  overflow: hidden;

  .blob {
    position: fixed;
    border-radius: 50%;
    filter: blur(90px);
    pointer-events: none;
    z-index: 0;
  }
  .blob--a {
    width: 520px; height: 520px;
    background: radial-gradient(circle, rgba(212,175,55,0.12) 0%, transparent 70%);
    top: -120px; left: -160px;
    animation: ${floatA} 14s ease-in-out infinite;
  }
  .blob--b {
    width: 480px; height: 480px;
    background: radial-gradient(circle, rgba(59,130,246,0.08) 0%, transparent 70%);
    bottom: -100px; right: -140px;
    animation: ${floatB} 18s ease-in-out infinite;
  }

  /* ── Card ── */
  .card {
    position: relative;
    z-index: 1;
    width: 100%;
    max-width: 520px;
    background: var(--navy-card);
    border: 1px solid rgba(212,175,55,0.18);
    border-radius: var(--radius-lg);
    padding: 40px 44px 36px;
    box-shadow: 0 24px 80px rgba(0,0,0,0.5), 0 0 0 1px rgba(255,255,255,0.03);

    @media (max-width: 540px) {
      padding: 28px 20px 24px;
    }
  }

  /* ── Header ── */
  .card__header {
    text-align: center;
    margin-bottom: 32px;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px; height: 52px;
    background: var(--gold-dim);
    border: 1px solid rgba(212,175,55,0.35);
    border-radius: 14px;
    color: var(--gold);
    margin-bottom: 16px;
  }

  .card__title {
    font-family: 'Cormorant Garamond', Georgia, serif;
    font-size: 2.1rem;
    font-weight: 700;
    color: var(--text);
    letter-spacing: 0.01em;
    margin: 0 0 6px;
    line-height: 1.15;
  }

  .card__sub {
    font-size: 0.88rem;
    color: var(--text-muted);
    margin: 0;
    line-height: 1.5;
  }

  /* ── Form ── */
  .card__form {
    display: flex;
    flex-direction: column;
    gap: 18px;
  }

  .grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    @media (max-width: 440px) { grid-template-columns: 1fr; }
  }

  /* ── Floating field ── */
  .field {
    position: relative;
  }

  .field__input {
    width: 100%;
    background: var(--navy-field);
    border: 1px solid rgba(255,255,255,0.08);
    border-radius: var(--radius-sm);
    padding: 22px 14px 8px;
    font-family: 'DM Sans', sans-serif;
    font-size: 0.93rem;
    color: var(--text);
    outline: none;
    transition: border-color 0.25s, box-shadow 0.25s;
    box-sizing: border-box;

    &:focus {
      border-color: var(--gold);
      box-shadow: 0 0 0 3px rgba(212,175,55,0.12);
    }
  }

  .field__label {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 0.88rem;
    color: var(--text-muted);
    pointer-events: none;
    transition: top 0.22s ease, font-size 0.22s ease, color 0.22s ease;
    white-space: nowrap;
  }

  .field__input:focus + .field__label,
  .field__input:not(:placeholder-shown) + .field__label {
    top: 10px;
    transform: none;
    font-size: 0.72rem;
    color: var(--gold);
    font-weight: 500;
  }

  .field__line {
    position: absolute;
    bottom: 0; left: 14px; right: 14px;
    height: 2px;
    background: var(--gold);
    border-radius: 2px;
    transform: scaleX(0);
    transform-origin: left;
    transition: transform 0.3s ease;
  }
  .field__input:focus ~ .field__line {
    transform: scaleX(1);
  }

  /* ── Eye button ── */
  .eye-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    color: var(--text-muted);
    cursor: pointer;
    padding: 4px;
    display: flex;
    align-items: center;
    transition: color 0.2s;
    &:hover { color: var(--gold); }
  }

  /* ── Notice ── */
  .notice {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    background: rgba(212,175,55,0.07);
    border: 1px solid rgba(212,175,55,0.22);
    border-radius: 8px;
    padding: 10px 14px;
    font-size: 0.78rem;
    color: #c9a93a;
    line-height: 1.5;

    svg { flex-shrink: 0; margin-top: 1px; }
  }

  /* ── Submit ── */
  .btn-submit {
    margin-top: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    padding: 14px 24px;
    background: linear-gradient(135deg, #d4af37 0%, #f0d060 50%, #c8990a 100%);
    border: none;
    border-radius: var(--radius-sm);
    font-family: 'DM Sans', sans-serif;
    font-size: 0.95rem;
    font-weight: 500;
    color: #0b0f1e;
    cursor: pointer;
    letter-spacing: 0.03em;
    transition: filter 0.2s;

    &:hover { filter: brightness(1.06); }

    svg { transition: transform 0.2s; }
    &:not(:disabled):hover svg { transform: translateX(3px); }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
  }

  .loader-spinner {
    width: 20px;
    height: 20px;
    border: 2px solid rgba(11, 15, 30, 0.3);
    border-top: 2px solid #0b0f1e;
    border-radius: 50%;
    animation: spin-submit 0.8s linear infinite;
  }

  @keyframes spin-submit {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* ── Feedback ── */
  .feedback {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 16px;
    border-radius: var(--radius-sm);
    font-size: 0.84rem;
    line-height: 1.5;

    svg { flex-shrink: 0; margin-top: 2px; }
    p { margin: 2px 0 0; font-size: 0.78rem; opacity: 0.8; }
  }
  .feedback--success {
    background: rgba(16,185,129,0.1);
    border: 1px solid rgba(16,185,129,0.3);
    color: #6ee7b7;
  }
  .feedback--error {
    background: rgba(248,113,113,0.08);
    border: 1px solid rgba(248,113,113,0.3);
    color: #fca5a5;
  }

  /* ── Footer ── */
  .card__footer {
    text-align: center;
    margin: 20px 0 0;
    font-size: 0.83rem;
    color: var(--text-muted);
  }

  .card__link {
    color: var(--gold);
    text-decoration: none;
    font-weight: 500;
    transition: color 0.2s;
    &:hover { color: var(--gold-light); text-decoration: underline; }
  }
`;

export default CreateAccountVisiteur;